import { useEffect, useState } from "react";
import api from "@/lib/api";
import { ROLE_LABELS } from "@/lib/constants";
import { KeyRound, Users2 } from "lucide-react";

// Static map of frontend UI items each role unlocks (kept in sync with Layout NAV_GROUPS).
const UI_MATRIX = {
  it_admin:            ["Dashboard","My Contracts","New Intake","Routing Pipeline","HCD Contract Central","Divisions & BUs","Compliance","Reports & BI","AI Assistant","Renewal Pipeline","Access Control","Sites & Vendors","Approval Workflows","Reviewer Roster","Evaluation Rules","Role Library","IT Admin"],
  general_counsel:     ["Dashboard","My Contracts","New Intake","Routing Pipeline","HCD Contract Central","Divisions & BUs","Compliance","Reports & BI","AI Assistant","Renewal Pipeline","Evaluation Rules","Reviewer Roster"],
  legal_manager:       ["Dashboard","My Contracts","New Intake","Director of Legal Team","Routing Pipeline","HCD Contract Central","Divisions & BUs","Reports & BI","AI Assistant","Renewal Pipeline","Evaluation Rules","Reviewer Roster"],
  attorney:            ["Dashboard","My Contracts","New Intake","Attorney Portal","Routing Pipeline","HCD Contract Central","Divisions & BUs","AI Assistant","Renewal Pipeline","Evaluation Rules"],
  contract_analyst:    ["Dashboard","My Contracts","New Intake","My Workspace","Routing Pipeline","HCD Contract Central","Divisions & BUs","AI Assistant"],
  compliance:          ["Dashboard","My Contracts","New Intake","Routing Pipeline","HCD Contract Central","Divisions & BUs","Compliance","Reports & BI","AI Assistant"],
  records_management:  ["Dashboard","My Contracts","New Intake","Routing Pipeline","HCD Contract Central","Divisions & BUs"],
  purchasing:          ["Dashboard","My Contracts","New Intake","Purchasing Portal","Routing Pipeline","Divisions & BUs"],
  department_director: ["Dashboard","My Contracts","New Intake","Divisions & BUs","Reports & BI","Access Control","Renewal Pipeline (own dept)"],
  department_admin:    ["Dashboard","My Contracts","New Intake","HCD Contract Central","Divisions & BUs","Access Control"],
  business_unit_rep:   ["Dashboard","My Contracts","New Intake","Divisions & BUs"],
  readonly_auditor:    ["Dashboard","HCD Contract Central","Reports & BI"],
  contract_manager:    ["Dashboard","My Contracts","New Intake","Routing Pipeline","HCD Contract Central","Divisions & BUs"],
};

// Endpoint groups by role (curated snapshot — authoritative check is /api/rbac/matrix).
const API_MATRIX = {
  it_admin:            "Full: /api/* (all admin routes, RBAC matrix, secrets, migrations)",
  general_counsel:     "Contracts read/write, approvals decide, workflows admin, evaluations finalize, reports, audit view",
  legal_manager:       "Contracts read/write, /workspace/manager-console, /workspace/bulk-assign, workflows read, reports",
  attorney:            "/portal/attorney, /portal/attorney/claim, contracts assigned to self, evaluations view + finalize",
  contract_analyst:    "/workspace/paralegal, contracts assigned to self, edit tracker, notes, checklist",
  compliance:          "/compliance/dashboard, red-flag clear, contracts read, checklist edits",
  records_management:  "Contracts read, download docs (any classification except Restricted requires legal role)",
  purchasing:          "/portal/purchasing, PO attach + view, no clause edits",
  department_director: "Contracts scoped to own dept, /rbac/delegations grant/revoke (dept)",
  department_admin:    "Same as director but no delegations",
  business_unit_rep:   "Submit intake, view own contracts, comment",
  readonly_auditor:    "GET only across the app; no write, no restricted docs",
  contract_manager:    "Same day-to-day as legal_manager but no manager-console",
};

export default function RoleLibrary() {
  const [catalog, setCatalog] = useState(null);
  const [matrix, setMatrix] = useState(null);
  useEffect(() => {
    api.get("/rbac/catalog").then(r => setCatalog(r.data)).catch(() => setCatalog({}));
    api.get("/rbac/matrix").then(r => setMatrix(r.data)).catch(() => setMatrix([]));
  }, []);
  const roles = catalog?.roles || Object.keys(UI_MATRIX);
  const modules = catalog?.modules || [];

  const forRole = (role) => (matrix || []).filter(m => m.role === role && m.allowed);

  return (
    <div className="p-6 space-y-4" data-testid="role-library-page">
      <div className="flex items-center gap-3 mb-2">
        <KeyRound className="w-5 h-5 text-[#005289]" />
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Administration · Role Catalog</div>
          <h1 className="text-2xl font-bold text-slate-900">Role Library</h1>
          <p className="text-sm text-slate-500 mt-1">Every role in the system, the sidebar items it unlocks, and the backend endpoints it can hit. Edit permissions in <a href="/admin/access" className="underline text-[#005289]">Access Control</a>.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {roles.map(r => {
          const perms = forRole(r);
          return (
            <div key={r} className="border border-slate-200 rounded-md bg-white" data-testid={`role-card-${r}`}>
              <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                <Users2 className="w-4 h-4 text-[#005289]"/>
                <div>
                  <div className="font-bold text-sm text-slate-900">{ROLE_LABELS[r] || r}</div>
                  <div className="font-mono text-[10px] text-slate-500">{r}</div>
                </div>
                <span className="ml-auto text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-600">{perms.length} rules</span>
              </div>
              <div className="p-4 space-y-3 text-xs">
                <section>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Sidebar items unlocked</div>
                  <div className="flex flex-wrap gap-1">
                    {(UI_MATRIX[r] || []).map(x => <span key={x} className="text-[10px] px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100">{x}</span>)}
                  </div>
                </section>
                <section>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Backend endpoints (summary)</div>
                  <div className="text-slate-700 leading-relaxed">{API_MATRIX[r] || "—"}</div>
                </section>
                {perms.length ? (
                  <section>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Permission matrix ({perms.length})</div>
                    <div className="text-[10px] text-slate-500 grid grid-cols-2 gap-x-3 gap-y-0.5">
                      {perms.slice(0, 20).map((p, i) => <div key={i}><span className="font-mono text-slate-700">{p.module}</span> · {p.action}</div>)}
                      {perms.length > 20 ? <div className="text-slate-400">…+{perms.length - 20} more</div> : null}
                    </div>
                  </section>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
